// Rates & History - Rate Search

import React, {useEffect, useRef, useState} from "react";
import {useAuth0, withAuthenticationRequired} from "@auth0/auth0-react";
import LaneService from "../services/LaneService";
import Select from "react-select";
import 'react-tooltip/dist/react-tooltip.css'
import { Tooltip as ReactTooltip } from "react-tooltip";
import CompanyLogo from "../assets/images/farelanes-logo-gold.png";
import DataTable from "react-data-table-component";
import UserService from "../services/UserService";
import {initAmplitude, sendAmplitudeData, setAmplitudeUserId} from "../common/amplitude";
import {PageLoader} from "./PageLoader";
import {PriceBlockComponent} from "./PriceBlock";
import {Tab, TabList, TabPanel, Tabs} from "react-tabs";
import Modal from "react-modal";
import {customStyles, equipmentTypeStyles, radiusStyles} from "../pages/Constants";
import AsyncSelect from 'react-select/async';
import {Rmaps} from "./RMaps";
import "../styles/CitySearch.css";


export const CitySearch = () => {
    const [state, setState] = useState({
        messaged: '',
        apiMessage: undefined,
        error: null,
        subscription: false,
        subscriptionType: 'Default'
    });


    const [searchHistoryData, setSearchHistoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmBeforeDelete, setConfirmBeforeDelete] = useState(() => {
        try {
            const stored = localStorage.getItem('rateSearch_confirmBeforeDelete');
            return stored === 'true';
        } catch {
            return false;
        }
    });
    const confirmBeforeDeleteRef = useRef(confirmBeforeDelete);
    confirmBeforeDeleteRef.current = confirmBeforeDelete;
    const [expandedRows, setExpandedRows] = useState({});
    const [editedData, setEditedData] = useState({});
    const [searching, setSearching] = useState({});
    const [queriedRows, setQueriedRows] = useState({}); // Track which rows have been queried
    
    // Single row search form state
    const [singleRowSearch, setSingleRowSearch] = useState({
        equipment: '',
        pickCity: '',
        pickRadius: '',
        dropCity: '',
        dropRadius: ''
    });
    const [singleRowSearching, setSingleRowSearching] = useState(false);
    const [singleRowResults, setSingleRowResults] = useState(null);
    
    // Modal state for REPORTS functionality
    const [modalIsOpen, setIsOpen] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [currentModalData, setCurrentModalData] = useState(null); // Track which row's data to show in modal


    const equipmentOptions = [
        {value: 'Dry Van', label: 'Dry Van'},
        {value: 'Flatbed', label: 'Flatbed'},
        {value: 'Reefer', label: 'Reefer'},
        {value: 'All', label: 'All'},
        {value: 'Auto Carrier', label: 'Auto Carrier'},
        {value: 'B-Train', label: 'B-Train'},
        {value: 'Box Truck', label: 'Box Truck'},
        {value: 'Cattle Trailer', label: 'Cattle Trailer'},
        {value: 'Conestoga', label: 'Conestoga'},
        {value: 'Container', label: 'Container'},
        {value: 'Conveyor', label: 'Conveyor'},
        {value: 'Double Drop', label: 'Double Drop'},
        {value: 'Drop Deck', label: 'Drop Deck'},
        {value: 'Dry Chassis', label: 'Dry Chassis'},
        {value: 'Dump Trailer', label: 'Dump Trailer'},
        {value: 'Hopper Bottom', label: 'Hopper Bottom'},
        {value: 'Lowboy', label: 'Lowboy'},
        {value: 'Maxi', label: 'Maxi'},
        {value: 'Moving Van', label: 'Moving Van'},
        {value: 'Pneumatic', label: 'Pneumatic'},
        {value: 'Power Only', label: 'Power Only'},
        {value: 'Reefer Double', label: 'Reefer Double'},
        {value: 'Removable Gooseneck', label: 'Removable Gooseneck'},
        {value: 'Sprinter', label: 'Sprinter'},
        {value: 'Step Deck', label: 'Step Deck'},
        {value: 'Straight Box Reefer', label: 'Straight Box Reefer'},
        {value: 'Straight Box', label: 'Straight Box'},
        {value: 'Stretch Trailer', label: 'Stretch Trailer'},
        {value: 'Tanker', label: 'Tanker'},
        {value: 'Truck and Trailer', label: 'Truck & Trailer'},
        {value: 'Walking Floor', label: 'Walking Floor'},
    ];


    const {user, getAccessTokenSilently} = useAuth0();


    // Load user subscription status and search history
    const loadUserData = async () => {
        try {
            const token = await getAccessTokenSilently();
            
            // First check subscription status
            const subscriptionResponse = await UserService.userSubscriptionStatusPair(token, user);
            
            setState(prev => ({
                ...prev,
                subscription: subscriptionResponse.data.status,
                subscriptionType: subscriptionResponse.data.subscriptionType
            }));


            // If user has subscription, load search history
            if (subscriptionResponse.data.status) {
                try {
                    const historyResponse = await LaneService.getUserRateSearchOptions(token);
                    
                    // Handle different response structures
                    let historyData = [];
                    if (historyResponse && historyResponse.data) {
                        historyData = historyResponse.data;
                    } else if (historyResponse && Array.isArray(historyResponse)) {
                        historyData = historyResponse;
                    } else if (historyResponse && historyResponse.collection) {
                        historyData = historyResponse.collection;
                    }
                    
                    // Format the data for the table if it's not already formatted
                    if (historyData && historyData.length > 0) {
                        const formattedData = historyData.map((item, index) => ({
                            id: item.id || `temp-${index}`, // Use MongoDB id if available, otherwise temp id
                            mongoId: item.id, // Preserve MongoDB id for deletion
                            equipment: item.equipment || item.equipment_type || 'Unknown',
                            pickCity: item.pick_city && item.pick_state ? `${item.pick_city}, ${getStateAbbreviation(item.pick_state)}` : 'Unknown',
                            dropCity: item.drop_city && item.drop_state ? `${item.drop_city}, ${getStateAbbreviation(item.drop_state)}` : 'Unknown',
                            pickRadius: item.pick_radius_miles || item.radius_miles || '100',
                            dropRadius: item.drop_radius_miles || item.radius_miles || '100',
                            // Preserve original fields for API calls
                            pick_city: item.pick_city,
                            pick_state: item.pick_state,
                            drop_city: item.drop_city,
                            drop_state: item.drop_state,
                            pick_radius_miles: item.pick_radius_miles || item.radius_miles,
                            drop_radius_miles: item.drop_radius_miles || item.radius_miles
                        }));
                        historyData = formattedData;
                    }
                    
                    setSearchHistoryData(historyData);
                } catch (historyError) {
                    console.error('Error loading search history:', historyError);
                    // Set empty array if search history fails
                    setSearchHistoryData([]);
                }
            }
        } catch (error) {
            console.error('Error loading user data:', error);
            setState(prev => ({
                ...prev,
                subscription: false
            }));
        } finally {
            setLoading(false);
        }
    };


    // Handle field changes for inline editing
    const handleFieldChange = (rowId, field, value) => {
        setEditedData(prev => ({
            ...prev,
            [rowId]: {
                ...prev[rowId],
                [field]: value
            }
        }));
    };

    // Handle delete row
    const handleDelete = async (rowId, rowData) => {
        // Confirm deletion when toggle is on (use ref to avoid stale closure) - applies to both saved and unsaved
        if (confirmBeforeDeleteRef.current && !window.confirm('Are you sure you want to delete this search history entry?')) {
            return;
        }

        // For unsaved entries (no MongoDB id), just remove from local state
        if (!rowData.mongoId) {
            setSearchHistoryData(prev => prev.filter(item => item.id !== rowId));
            setExpandedRows(prev => {
                const newState = { ...prev };
                delete newState[rowId];
                return newState;
            });
            return;
        }

        try {
            const token = await getAccessTokenSilently();
            await LaneService.deleteRateSearchHistory(rowData.mongoId, token);
            
            // Remove from local state
            setSearchHistoryData(prev => prev.filter(item => item.id !== rowId));
            
            // Also remove from expanded rows if present
            setExpandedRows(prev => {
                const newState = { ...prev };
                delete newState[rowId];
                return newState;
            });
        } catch (error) {
            console.error('Error deleting search history:', error);
            alert('Failed to delete search history entry. Please try again.');
        }
    };


    // Handle single row search form changes
    const handleSingleRowSearchChange = (field, value) => {
        setSingleRowSearch(prev => ({
            ...prev,
            [field]: value
        }));
    };


    // Handle single row search execution
    const handleSingleRowSearch = async () => {
        if (singleRowSearching) return;
        
        // Validate required fields
        if (!singleRowSearch.equipment || !singleRowSearch.pickCity || !singleRowSearch.dropCity || !singleRowSearch.pickRadius || !singleRowSearch.dropRadius) {
            alert('Please fill in all fields before searching.');
            return;
        }
        
        setSingleRowSearching(true);
        
        try {
            const token = await getAccessTokenSilently();
            
            // Parse city and state from the search form
            const pickCityData = singleRowSearch.pickCity.split(', ');
            const pickCity = pickCityData[0];
            const pickState = pickCityData[1];
            
            const dropCityData = singleRowSearch.dropCity.split(', ');
            const dropCity = dropCityData[0];
            const dropState = dropCityData[1];
            
            const equipment = singleRowSearch.equipment;
            const pickRadius = parseInt(singleRowSearch.pickRadius);
            const dropRadius = parseInt(singleRowSearch.dropRadius);
            
            // Convert state abbreviations to full state names for API
            const fullPickState = getFullStateName(pickState);
            const fullDropState = getFullStateName(dropState);
            
            console.log('Single row search with:', { pickCity, pickState, dropCity, dropState, equipment, pickRadius, dropRadius });
            
            // Use the LaneService method with separate pick and drop radii (search_source=rate_search so it appears in this page's history only)
            const response = await LaneService.getLaneStatistics(
                pickCity, 
                fullPickState, 
                dropCity, 
                fullDropState, 
                equipment, 
                pickRadius, 
                dropRadius, 
                120, // days
                token,
                'rate_search'
            );


            console.log('Single row search response:', response);
            
            // Create a result with all the data needed for display
            const searchResult = {
                ...response.data,
                // Create chart data from the response
                chartData: [
                    {
                        name: '4 Months',
                        uv: response.data?.thirtyDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.thirtyDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.thirtyDaysAverage?.averageLoadRate || 0,
                    },
                    {
                        name: '2 Months',
                        uv: response.data?.fifteenDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.fifteenDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.fifteenDaysAverage?.averageLoadRate || 0,
                    },
                    {
                        name: '30 Days',
                        uv: response.data?.sevenDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.sevenDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.sevenDaysAverage?.averageLoadRate || 0,
                    },
                    {
                        name: '7 Days',
                        uv: response.data?.threeDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.threeDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.threeDaysAverage?.averageLoadRate || 0,
                    },
                    {
                        name: '3 Days',
                        uv: response.data?.oneDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.oneDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.oneDaysAverage?.averageLoadRate || 0,
                    }
                ],
                // Include the detailed collection data for the modal
                thirtyDaysAverage: response.data?.thirtyDaysAverage,
                fifteenDaysAverage: response.data?.fifteenDaysAverage,
                sevenDaysAverage: response.data?.sevenDaysAverage,
                threeDaysAverage: response.data?.threeDaysAverage,
                oneDaysAverage: response.data?.oneDaysAverage
            };
            
            setSingleRowResults(searchResult);
            
            // Update state with quota information from API response
            setState(prev => ({
                ...prev,
                apiMessage: {
                    quotaMessage: response.data?.quotaMessage,
                    quotaFull: response.data?.quotaFull
                }
            }));
            
            // Add this search to the history
            const newEntry = {
                id: Date.now(),
                equipment: singleRowSearch.equipment,
                pickCity: singleRowSearch.pickCity,
                dropCity: singleRowSearch.dropCity,
                pickRadius: singleRowSearch.pickRadius,
                dropRadius: singleRowSearch.dropRadius,
                lastSearched: new Date().toISOString(),
                isNewEntry: true
            };


            setSearchHistoryData(prev => [newEntry, ...prev]);
            
            // Don't auto-expand the new entry - let user click to expand if they want
            
        } catch (error) {
            console.error('Single row search error:', error);
            setSingleRowResults({ error: true, message: 'Search failed' });
            
            // Still add to history even on error, but don't store in expandedRows
            const newEntry = {
                id: Date.now(),
                equipment: singleRowSearch.equipment,
                pickCity: singleRowSearch.pickCity,
                dropCity: singleRowSearch.dropCity,
                pickRadius: singleRowSearch.pickRadius,
                dropRadius: singleRowSearch.dropRadius,
                lastSearched: new Date().toISOString(),
                isNewEntry: true
            };


            setSearchHistoryData(prev => [newEntry, ...prev]);
            
            // Don't auto-expand the new entry even on error - let user click to expand if they want
        } finally {
            setSingleRowSearching(false);
        }
    };






    // Handle search execution
    const handleSearch = async (rowId, rowData) => {
        if (searching[rowId]) return;
        
        setSearching(prev => ({ ...prev, [rowId]: true }));
        setQueriedRows(prev => {
            const newState = { ...prev, [rowId]: true };
            console.log('Setting queriedRows for rowId:', rowId, 'new state:', newState);
            return newState;
        }); // Mark row as queried immediately
        
        try {
            const token = await getAccessTokenSilently();
            
            // Get the current values (edited or original)
            const currentPickCity = editedData[rowId]?.pickCity || rowData.pickCity;
            const currentDropCity = editedData[rowId]?.dropCity || rowData.dropCity;
            const currentEquipment = editedData[rowId]?.equipment || rowData.equipment;
            const currentPickRadius = editedData[rowId]?.pickRadius || rowData.pickRadius || '100';
            const currentDropRadius = editedData[rowId]?.dropRadius || rowData.dropRadius || '100';
            
            // Parse city and state - handle both "City, State" format and separate fields
            let pickCity, pickState, dropCity, dropState;
            
            if (currentPickCity && currentPickCity.includes(',')) {
                // Format: "City, State"
                const pickCityData = currentPickCity.split(', ');
                pickCity = pickCityData[0];
                pickState = pickCityData[1];
            } else {
                // Try to get from original data structure
                pickCity = rowData.pick_city || currentPickCity;
                pickState = rowData.pick_state || 'Unknown';
            }
            
            if (currentDropCity && currentDropCity.includes(',')) {
                // Format: "City, State"
                const dropCityData = currentDropCity.split(', ');
                dropCity = dropCityData[0];
                dropState = dropCityData[1];
            } else {
                // Try to get from original data structure
                dropCity = rowData.drop_city || currentDropCity;
                dropState = rowData.drop_state || 'Unknown';
            }
            
            const equipment = currentEquipment;
            const pickRadius = parseInt(currentPickRadius);
            const dropRadius = parseInt(currentDropRadius);
            
            // Convert state abbreviations to full state names for API
            const fullPickState = getFullStateName(pickState);
            const fullDropState = getFullStateName(dropState);
            
            console.log('Searching with:', { pickCity, pickState, dropCity, dropState, equipment, pickRadius, dropRadius });
            console.log('Converted to full state names:', { pickCity, fullPickState, dropCity, fullDropState });
            console.log('Original rowData:', rowData);
            console.log('Edited data for rowId:', editedData[rowId]);
            
            // Use the correct LaneService method with separate pick and drop radii (search_source=rate_search so it appears in this page's history only)
            const response = await LaneService.getLaneStatistics(
                pickCity, 
                fullPickState, 
                dropCity, 
                fullDropState, 
                equipment, 
                pickRadius, 
                dropRadius, 
                120, // days
                token,
                'rate_search'
            );


            console.log('Search response:', response);
            
            // Create a result with all the data needed for both expandable rows and modal
            const testResult = {
                ...response.data,
                // Create chart data from the response
                chartData: [
                    {
                        name: '4 Months',
                        uv: response.data?.thirtyDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.thirtyDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.thirtyDaysAverage?.averageLoadRate || 0,
                    },
                    {
                        name: '2 Months',
                        uv: response.data?.fifteenDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.fifteenDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.fifteenDaysAverage?.averageLoadRate || 0,
                    },
                    {
                        name: '30 Days',
                        uv: response.data?.sevenDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.sevenDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.sevenDaysAverage?.averageLoadRate || 0,
                    },
                    {
                        name: '7 Days',
                        uv: response.data?.threeDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.threeDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.threeDaysAverage?.averageLoadRate || 0,
                    },
                    {
                        name: '3 Days',
                        uv: response.data?.oneDaysAverage?.averageLoadRate || 0,
                        pv: response.data?.oneDaysAverage?.averageLoadRate || 0,
                        amt: response.data?.oneDaysAverage?.averageLoadRate || 0,
                    }
                ],
                // Include the detailed collection data for the modal
                thirtyDaysAverage: response.data?.thirtyDaysAverage,
                fifteenDaysAverage: response.data?.fifteenDaysAverage,
                sevenDaysAverage: response.data?.sevenDaysAverage,
                threeDaysAverage: response.data?.threeDaysAverage,
                oneDaysAverage: response.data?.oneDaysAverage
            };
            
            console.log('Setting expanded row data:', testResult);
            
            // Check if there are actual changes before creating a new entry
            const hasChanges = editedData[rowId] && Object.keys(editedData[rowId]).length > 0;
            
            if (hasChanges) {
                // Add a NEW search history entry with edited values
                const newEntry = {
                    id: Date.now(), // Generate new unique ID
                    equipment: editedData[rowId]?.equipment || rowData.equipment,
                    pickCity: editedData[rowId]?.pickCity || rowData.pickCity,
                    dropCity: editedData[rowId]?.dropCity || rowData.dropCity,
                    pickRadius: editedData[rowId]?.pickRadius || rowData.pickRadius || '100',
                    dropRadius: editedData[rowId]?.dropRadius || rowData.dropRadius || '100',
                    lastSearched: new Date().toISOString(), // Set current timestamp
                    isNewEntry: true // Flag to highlight this as a new entry
                };


                setSearchHistoryData(prev => {
                    // Add new entry at the top of the list
                    return [newEntry, ...prev];
                });


                // Store results using the NEW entry's ID, not the old rowId
                setExpandedRows(prev => {
                    const newState = { 
                        ...prev, 
                        [newEntry.id]: testResult // Use newEntry.id instead of rowId
                    };
                    console.log('New expanded rows state with new entry ID:', newState);
                    return newState;
                });
                
                // Update state with quota information from API response
                setState(prev => ({
                    ...prev,
                    apiMessage: {
                        quotaMessage: response.data?.quotaMessage,
                        quotaFull: response.data?.quotaFull
                    }
                }));


                // Auto-expand the new entry
                setTimeout(() => {
                    const newRowElement = document.querySelector(`[data-row-id="${newEntry.id}"]`);
                    if (newRowElement) {
                        newRowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);


                // Show success message
                console.log('New search history entry added:', newEntry);
            } else {
                // No changes, just store results for the existing row
                setExpandedRows(prev => {
                    const newState = { 
                        ...prev, 
                        [rowId]: testResult // Use existing rowId
                    };
                    console.log('Updated existing row results:', newState);
                    return newState;
                });
                
                // Update state with quota information from API response
                setState(prev => ({
                    ...prev,
                    apiMessage: {
                        quotaMessage: response.data?.quotaMessage,
                        quotaFull: response.data?.quotaFull
                    }
                }));
                
                console.log('No changes detected, using existing row');
            }






            // Clear the edited data for this row since it's now saved
            setEditedData(prev => {
                const newState = { ...prev };
                delete newState[rowId];
                return newState;
            });
        } catch (error) {
            console.error('Search error:', error);
            
            // Check if there are actual changes before creating a new entry
            const hasChanges = editedData[rowId] && Object.keys(editedData[rowId]).length > 0;
            
            if (hasChanges) {
                // Even on error, add a NEW search history entry with edited values to preserve changes
                const newEntry = {
                    id: Date.now(), // Generate new unique ID
                    equipment: editedData[rowId]?.equipment || rowData.equipment,
                    pickCity: editedData[rowId]?.pickCity || rowData.pickCity,
                    dropCity: editedData[rowId]?.dropCity || rowData.dropCity,
                    pickRadius: editedData[rowId]?.pickRadius || rowData.pickRadius || '100',
                    dropRadius: editedData[rowId]?.dropRadius || rowData.dropRadius || '100',
                    lastSearched: new Date().toISOString(), // Set current timestamp
                    isNewEntry: true // Flag to highlight this as a new entry
                };


                setSearchHistoryData(prev => {
                    // Add new entry at the top of the list
                    return [newEntry, ...prev];
                });


                // Also store results using the new entry ID for error case
                setExpandedRows(prev => {
                    const newState = { 
                        ...prev, 
                        [newEntry.id]: { error: true, message: 'Search failed but entry was created' }
                    };
                    return newState;
                });
            } else {
                // No changes, just store error for existing row
                setExpandedRows(prev => {
                    const newState = { 
                        ...prev, 
                        [rowId]: { error: true, message: 'Search failed' }
                    };
                    return newState;
                });
            }
        } finally {
            setSearching(prev => ({ ...prev, [rowId]: false }));
        }
    };


    // Function to get state abbreviation
    const getStateAbbreviation = (stateName) => {
        const stateMap = {
            'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
            'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
            'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
            'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
            'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
            'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
            'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
            'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
            'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
            'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
        };
        return stateMap[stateName] || stateName; // Return abbreviation if found, otherwise return original
    };


    // Function to convert state abbreviation to full state name
    const getFullStateName = (stateAbbrev) => {
        const abbreviationMap = {
            'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
            'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
            'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
            'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
            'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
            'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
            'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
            'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
            'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
            'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
        };
        return abbreviationMap[stateAbbrev] || stateAbbrev; // Return full name if found, otherwise return original
    };


    // Modal functions for REPORTS functionality
    function openModal30Days(rowData = null) {
        console.log('openModal30Days called with rowData:', rowData);
        if (rowData) {
            setCurrentModalData(rowData);
        }
        setTabIndex(0);
        setIsOpen(true);
    }


    function openModal15Days(rowData = null) {
        console.log('openModal15Days called with rowData:', rowData);
        if (rowData) {
            setCurrentModalData(rowData);
        }
        setTabIndex(1);
        setIsOpen(true);
    }


    function openModal7Days(rowData = null) {
        console.log('openModal7Days called with rowData:', rowData);
        if (rowData) {
            setCurrentModalData(rowData);
        }
        setTabIndex(2);
        setIsOpen(true);
    }


    function openModal3Days(rowData = null) {
        console.log('openModal3Days called with rowData:', rowData);
        if (rowData) {
            setCurrentModalData(rowData);
        }
        setTabIndex(3);
        setIsOpen(true);
    }


    function openModal1Days(rowData = null) {
        console.log('openModal1Days called with rowData:', rowData);
        if (rowData) {
            setCurrentModalData(rowData);
        }
        setTabIndex(4);
        setIsOpen(true);
    }


    // Helper function to get collection data from a time period object
    const getCollectionData = (timePeriodData) => {
        if (!timePeriodData) {
            console.log('getCollectionData - no data provided');
            return [];
        }
        
        // If it has a collection property, use that
        if (timePeriodData.collection && Array.isArray(timePeriodData.collection)) {
            console.log('getCollectionData - using collection property, length:', timePeriodData.collection.length);
            return timePeriodData.collection;
        }
        
        // If the timePeriodData itself is an array, use it directly
        if (Array.isArray(timePeriodData)) {
            console.log('getCollectionData - data is array, length:', timePeriodData.length);
            return timePeriodData;
        }
        
        // Check if there's a 'data' property that contains the array
        if (timePeriodData.data && Array.isArray(timePeriodData.data)) {
            console.log('getCollectionData - using data property, length:', timePeriodData.data.length);
            return timePeriodData.data;
        }
        
        // Log the structure to help debug
        console.log('getCollectionData - unexpected structure:', timePeriodData);
        console.log('getCollectionData - keys:', Object.keys(timePeriodData));
        
        return [];
    };

    // Get current results for modal (either from currentModalData, single row search, or expanded rows)
    const getCurrentResults = () => {
        // First priority: currentModalData (explicitly set when opening modal)
        if (currentModalData && !currentModalData.error) {
            console.log('getCurrentResults - using currentModalData:', currentModalData);
            console.log('thirtyDaysAverage:', currentModalData.thirtyDaysAverage);
            console.log('thirtyDaysAverage.collection:', currentModalData.thirtyDaysAverage?.collection);
            return currentModalData;
        }
        
        // Second priority: singleRowResults
        if (singleRowResults && !singleRowResults.error) {
            console.log('getCurrentResults - using singleRowResults:', singleRowResults);
            console.log('thirtyDaysAverage:', singleRowResults.thirtyDaysAverage);
            console.log('thirtyDaysAverage.collection:', singleRowResults.thirtyDaysAverage?.collection);
            return singleRowResults;
        }
        
        // Fall back to the first expanded row result
        const expandedResult = Object.values(expandedRows).find(row => row && !row.error);
        console.log('getCurrentResults - using expandedResult:', expandedResult);
        if (expandedResult) {
            console.log('thirtyDaysAverage:', expandedResult.thirtyDaysAverage);
            console.log('thirtyDaysAverage.collection:', expandedResult.thirtyDaysAverage?.collection);
        }
        return expandedResult || null;
    };


    function afterOpenModal() {
        // references are now sync'd and can be accessed.
    }


    function closeModal() {
        setIsOpen(false);
        setCurrentModalData(null); // Clear the modal data when closing
    }


    // Function to load city options for AsyncSelect
    const loadCityOptions = async (inputValue) => {
        try {
            const token = await getAccessTokenSilently();
            
            // If no input or input is less than 3 characters, load default popular cities
            if (!inputValue || inputValue.length < 3) {
                // Load default popular cities for initial display
                const response = await LaneService.getCityOptions('', token);
                return response.map(city => ({ 
                    value: `${city.city}, ${city.stateName}`, 
                    label: `${city.city}, ${city.stateName}` 
                }));
            }
            
            // Load cities based on search input
            const response = await LaneService.getCityOptions(inputValue, token);
            return response.map(city => ({ 
                value: `${city.city}, ${city.stateName}`, 
                label: `${city.city}, ${city.stateName}` 
            }));
        } catch (error) {
            console.error('Error loading city options:', error);
            return [];
        }
    };


    // Data table columns for detailed reports modal
    const reportColumns = [
        {
            name: 'Equipment',
            selector: row => row.equipment,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.equipment}</div>
        },
        {
            name: 'Pick City',
            selector: row => row.pick_city,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.pick_city}</div>
        },
        {
            name: 'Pick State',
            selector: row => row.pick_state,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.pick_state}</div>
        },
        {
            name: 'Drop City',
            selector: row => row.drop_city,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.drop_city}</div>
        },
        {
            name: 'Drop State',
            selector: row => row.drop_state,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.drop_state}</div>
        },
        {
            name: 'Pick Zip',
            selector: row => row.pick_zip,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.pick_zip}</div>
        },
        {
            name: 'Drop Zip',
            selector: row => row.drop_zip,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.drop_zip}</div>
        },
        {
            name: 'Pick Date',
            selector: row => row.pick_date,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.pick_date}</div>
        },
        {
            name: 'Drop Date',
            selector: row => row.drop_date,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.drop_date}</div>
        },
        {
            name: 'Miles',
            selector: row => row.miles,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.miles}</div>
        },
        {
            name: 'Lane Rate',
            selector: row => row.lane_rate,
            sortable: true,
            cell: row => <div className="top-routes-text">$ {row.lane_rate}</div>
        },
        {
            name: 'Line Haul Amt',
            selector: row => row.line_haul_amount,
            sortable: true,
            cell: row => <div className="top-routes-text">$ {row.line_haul_amount}</div>
        },
        {
            name: 'Total Stops',
            selector: row => row.number_of_stops,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.number_of_stops}</div>
        },
        {
            name: 'Fuel Surcharge',
            selector: row => row.fuel_surcharge,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.fuel_surcharge}</div>
        },
        {
            name: 'Lumper',
            selector: row => row.lumper,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.lumper}</div>
        },
        {
            name: 'Other Fee',
            selector: row => row.other_fee,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.other_fee}</div>
        },
        {
            name: 'Pallet Charge',
            selector: row => row.pallet_charge,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.pallet_charge}</div>
        },
        {
            name: 'Truck Order',
            selector: row => row.truck_order,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.truck_order}</div>
        },
        {
            name: 'Detention',
            selector: row => row.detention,
            sortable: true,
            cell: row => <div className="top-routes-text">{row.detention}</div>
        }
    ];


    // Data table columns for search history
    const columns = [
        {
            name: 'EQUIPMENT',
            selector: row => row.equipment,
            sortable: true,
            cell: row => (
                <div className="equipment-select-container">
                    <Select
                        options={equipmentOptions}
                        value={(() => {
                            const eq = editedData[row.id]?.equipment || row.equipment;
                            return eq ? { value: eq, label: eq } : null;
                        })()}
                        onChange={(option) => handleFieldChange(row.id, 'equipment', option?.value ?? '')}
                        isSearchable={true}
                        placeholder="Select"
                        menuPlacement="auto"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        classNamePrefix="equipment-select"
                        styles={{
                            container: (provided) => ({
                                ...provided,
                                width: '100%'
                            }),
                            control: (provided) => ({
                                ...provided,
                                backgroundColor: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                                minHeight: '38px',
                                cursor: 'pointer',
                                width: '100%'
                            }),
                            valueContainer: (provided) => ({
                                ...provided,
                                flex: 1,
                                padding: '2px 8px'
                            }),
                            indicatorSeparator: () => ({ display: 'none' }),
                            dropdownIndicator: (provided) => ({
                                ...provided,
                                padding: '6px 8px',
                                color: '#7D7D7D'
                            }),
                            singleValue: (provided) => ({
                                ...provided,
                                color: '#495057'
                            }),
                            menu: (provided) => ({
                                ...provided,
                                backgroundColor: 'white',
                                border: '1px solid #dee2e6',
                                zIndex: 9999
                            }),
                            menuPortal: (provided) => ({
                                ...provided,
                                zIndex: 9999
                            })
                        }}
                    />
                    {row.isNewEntry && (
                        <div className="new-entry-badge" title="New entry">NEW</div>
                    )}
                </div>
            ),
            // minWidth: '180px'
        },
        {
            name: 'PICK CITY',
            selector: row => row.pickCity,
            sortable: true,
            cell: row => (
                <div className="pick-city-container">
                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={loadCityOptions}
                        value={{value: editedData[row.id]?.pickCity || row.pickCity, label: editedData[row.id]?.pickCity || row.pickCity}}
                        onChange={(option) => {
                            if (option) {
                                handleFieldChange(row.id, 'pickCity', option.value);
                            }
                        }}
                        placeholder="Search..."
                        menuPlacement="auto"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        classNamePrefix="city-select"
                        styles={{
                            container: (provided) => ({
                                ...provided,
                                width: '100%'
                            }),
                            control: (provided, state) => ({
                                ...provided,
                                backgroundColor: 'transparent',
                                background: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                                minHeight: '38px',
                                cursor: 'pointer',
                                width: '100%',
                                '&:hover': { backgroundColor: 'transparent', border: 'none' },
                                '&:focus': { backgroundColor: 'transparent', border: 'none' }
                            }),
                            valueContainer: (provided) => ({ ...provided, backgroundColor: 'transparent', flex: 1, padding: '2px 8px' }),
                            input: (provided) => ({ ...provided, backgroundColor: 'transparent' }),
                            indicatorSeparator: () => ({ display: 'none' }),
                            dropdownIndicator: (provided) => ({ ...provided, padding: '6px 8px', color: '#7D7D7D' }),
                            menu: (provided) => ({ ...provided, backgroundColor: 'white', border: '1px solid #dee2e6', zIndex: 9999 }),
                            menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
                        }}
                    />
                </div>
            ),
            // minWidth: '220px'
        },
        {
            name: 'RADIUS',
            selector: row => row.pickRadius,
            sortable: true,
            cell: row => (
                <div className="pick-radius-container">
                    <Select
                        options={[
                            {value: '50', label: '50 miles'},
                            {value: '100', label: '100 miles'},
                            {value: '150', label: '150 miles'},
                            {value: '200', label: '200 miles'},
                            {value: '250', label: '250 miles'}
                        ]}
                        value={{value: editedData[row.id]?.pickRadius || row.pickRadius || '100', label: `${editedData[row.id]?.pickRadius || row.pickRadius || '100'} miles`}}
                        onChange={(option) => handleFieldChange(row.id, 'pickRadius', option.value)}
                        isSearchable={true}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        classNamePrefix="pick-radius-select"
                        styles={{
                            container: (provided) => ({ ...provided, width: '100%' }),
                            control: (provided) => ({ ...provided, minHeight: '38px', cursor: 'pointer', width: '100%', backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }),
                            valueContainer: (provided) => ({ ...provided, flex: 1, padding: '2px 8px' }),
                            indicatorSeparator: () => ({ display: 'none' }),
                            dropdownIndicator: (provided) => ({ ...provided, padding: '6px 8px', color: '#7D7D7D' }),
                            menu: (provided) => ({ ...provided, backgroundColor: 'white', border: '1px solid #dee2e6', zIndex: 9999 }),
                            menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
                        }}
                    />
                </div>
            ),
            // minWidth: '140px'
        },
        {
            name: 'DROP CITY',
            selector: row => row.dropCity,
            sortable: true,
            cell: row => (
                <div className="pick-city-container">
                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={loadCityOptions}
                        value={{value: editedData[row.id]?.dropCity || row.dropCity, label: editedData[row.id]?.dropCity || row.dropCity}}
                        onChange={(option) => {
                            if (option) {
                                handleFieldChange(row.id, 'dropCity', option.value);
                            }
                        }}
                        placeholder="Search..."
                        menuPlacement="auto"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        classNamePrefix="city-select"
                        styles={{
                            container: (provided) => ({
                                ...provided,
                                width: '100%'
                            }),
                            control: (provided, state) => ({
                                ...provided,
                                backgroundColor: 'transparent',
                                background: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                                minHeight: '38px',
                                cursor: 'pointer',
                                width: '100%',
                                '&:hover': { backgroundColor: 'transparent', border: 'none' },
                                '&:focus': { backgroundColor: 'transparent', border: 'none' }
                            }),
                            valueContainer: (provided) => ({ ...provided, backgroundColor: 'transparent', flex: 1, padding: '2px 8px' }),
                            input: (provided) => ({ ...provided, backgroundColor: 'transparent' }),
                            indicatorSeparator: () => ({ display: 'none' }),
                            dropdownIndicator: (provided) => ({ ...provided, padding: '6px 8px', color: '#7D7D7D' }),
                            menu: (provided) => ({ ...provided, backgroundColor: 'white', border: '1px solid #dee2e6', zIndex: 9999 }),
                            menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
                        }}
                    />
                    {row.isNewEntry && (
                        <div className="timestamp-badge" title={`Created: ${new Date(row.lastSearched).toLocaleString()}`}>
                            {new Date(row.lastSearched).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </div>
                    )}
                </div>
            ),
            // minWidth: '220px'
        },
        {
            name: 'RADIUS',
            selector: row => row.dropRadius,
            sortable: true,
            cell: row => (
                <div className="drop-radius-container">
                    <Select
                        options={[
                            {value: '50', label: '50 miles'},
                            {value: '100', label: '100 miles'},
                            {value: '150', label: '150 miles'},
                            {value: '200', label: '200 miles'},
                            {value: '250', label: '250 miles'}
                        ]}
                        value={{value: editedData[row.id]?.dropRadius || row.dropRadius || '100', label: `${editedData[row.id]?.dropRadius || row.dropRadius || '100'} miles`}}
                        onChange={(option) => handleFieldChange(row.id, 'dropRadius', option.value)}
                        isSearchable={true}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        classNamePrefix="drop-radius-select"
                        styles={{
                            container: (provided) => ({ ...provided, width: '100%' }),
                            control: (provided) => ({ ...provided, minHeight: '38px', cursor: 'pointer', width: '100%', backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }),
                            valueContainer: (provided) => ({ ...provided, flex: 1, padding: '2px 8px' }),
                            indicatorSeparator: () => ({ display: 'none' }),
                            dropdownIndicator: (provided) => ({ ...provided, padding: '6px 8px', color: '#7D7D7D' }),
                            menu: (provided) => ({ ...provided, backgroundColor: 'white', border: '1px solid #dee2e6', zIndex: 9999 }),
                            menuPortal: (provided) => ({ ...provided, zIndex: 9999 })
                        }}
                    />
                </div>
            ),
            // minWidth: '140px'
        },


        {
            name: '',
            selector: row => row.id,
            sortable: false,
            cell: row => (
                <div className="action-buttons-container" style={{ 
                    display: 'flex', 
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    gap: '8px', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end',
                    width: '100%'
                }}>
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDelete(row.id, row);
                        }}
                        style={{
                            padding: '2px 8px',
                            fontSize: '16px',
                            lineHeight: '1',
                            minWidth: 'auto',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px',
                            border: 'none',
                            cursor: 'pointer',
                            flexShrink: 0
                        }}
                        title="Delete this entry"
                    >
                        ×
                    </button>
                    <button
                        className={`${editedData[row.id] && Object.keys(editedData[row.id]).length > 0 ? 'my-button-warning' : 'my-button'}`}
                        onClick={() => handleSearch(row.id, row)}
                        disabled={searching[row.id]}
                        style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                        {searching[row.id] ? (
                            <span className="spinner-border spinner-border-sm me-2"></span>
                        ) : (
                            <span>QUERY</span>
                        )}
                        {editedData[row.id] && Object.keys(editedData[row.id]).length > 0 ? '*' : ''}
                    </button>
                </div>
            )
            // ,
            // minWidth: '150px'
        }
    ];


    // Custom table styles
    const customTableStyles = {
        table: {
            style: {
                tableLayout: 'auto',
                width: '100%',
                minWidth: '800px'
            }
        },
        headCells: {
            // style: {
                // backgroundColor: '#f8f9fa',
                // color: '#495057',
                // fontWeight: '600',
                // fontSize: '12px',
                // textTransform: 'uppercase',
                // padding: '12px 8px',
                // textAlign: 'center',
                // borderBottom: '2px solid #dee2e6',
                // minWidth: '100px',
                // whiteSpace: 'nowrap'
            // }
        },
        headRow: {
            // style: {
            //     backgroundColor: '#f8f9fa'
            // }
        },
        cells: {
            style: {
                // padding: '12px 8px',
                // textAlign: 'center',
                // fontSize: '14px',
                // minWidth: '100px',
                whiteSpace: 'nowrap'
            }
        }
    };


    // Expandable row component
    const ExpandableComponent = ({ data }) => {
        const searchResults = expandedRows[data.id];
        
        if (!searchResults) return null;


        // Handle error case
        if (searchResults.error) {
            return (
                <div className="expandable-row">
                    <div className="row justify-content-center">
                        <div className="col-12 col-xl-10">
                            <div className="alert alert-warning text-center">
                                <h6>Search Failed</h6>
                                <p>{searchResults.message}</p>
                                <p>Entry was created but search could not be completed.</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }


        return (
            <div className="expandable-row">
                <div className="row justify-content-center">
                    <div className="col-12 col-xl-10">
                        <h6 className="mb-3 text-center">
                            QUERY Results for: {data.equipment} | {data.pickCity} → {data.dropCity}
                        </h6>
                        
                        {/* Price Blocks Row */}
                        <div className="row mb-3 justify-content-center">

                        	{/* Labels 
                        	<div className="col-6 col-md-4 col-lg-2 mb-2">
                            	<div className="">
        							<div className="">
        								<div className="lookup-card">
            								<div className="card-body">
                								<div className={"special-card rate-search-label"}>
													<p className="align-content-center">Duration:</p>
													<h6>Cost per Mile:</h6>
													<h6>Cost per Load:</h6>
													<hr/>
													<small>Average Load:</small>
													<small>Avg. dist from req pick up:</small>
													<small>Avg. dist from req drop off:</small>
												</div>
											</div>
										</div>
									</div>
								</div>
                            </div>*/}

                            <div className="col-6 col-md-4 col-lg-2 mb-2">
                                <PriceBlockComponent 
                                    object={{
                                        days: '4 Months',
                                        laneStatistics: searchResults.thirtyDaysAverage,
                                        subscriptionType: state.subscriptionType,
                                        onClick: () => openModal30Days(searchResults)
                                    }}
                                />
                            </div>
                            <div className="col-6 col-md-4 col-lg-2 mb-2">
                                <PriceBlockComponent 
                                    object={{
                                        days: '2 Months',
                                        laneStatistics: searchResults.fifteenDaysAverage,
                                        subscriptionType: state.subscriptionType,
                                        onClick: () => openModal15Days(searchResults)
                                    }}
                                />
                            </div>
                            <div className="col-6 col-md-4 col-lg-2 mb-2">
                                <PriceBlockComponent 
                                    object={{
                                        days: '30 Days',
                                        laneStatistics: searchResults.sevenDaysAverage,
                                        subscriptionType: state.subscriptionType,
                                        onClick: () => openModal7Days(searchResults)
                                    }}
                                />
                            </div>
                            <div className="col-6 col-md-4 col-lg-2 mb-2">
                                <PriceBlockComponent 
                                    object={{
                                        days: '7 Days',
                                        laneStatistics: searchResults.threeDaysAverage,
                                        subscriptionType: state.subscriptionType,
                                        onClick: () => openModal3Days(searchResults)
                                    }}
                                />
                            </div>
                            <div className="col-6 col-md-4 col-lg-2 mb-2">
                                <PriceBlockComponent 
                                    object={{
                                        days: '3 Days',
                                        laneStatistics: searchResults.oneDaysAverage,
                                        subscriptionType: state.subscriptionType,
                                        onClick: () => openModal1Days(searchResults)
                                    }}
                                />
                            </div>
                        </div>






                        {/* Line Chart */}
                        {searchResults.chartData && searchResults.chartData.length > 0 && (
                            <div className="row mt-4 justify-content-center">
                                <div className="col-12 col-xl-10">
                                    <div className="card shadow-sm">
                                        <div className="card-body">
                                            <h6 className="mb-3 text-center">Price Trends Over Time</h6>
                                            <Rmaps 
                                                ticker={{
                                                    dataMaps: {
                                                        mapsData: searchResults.chartData
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };


    useEffect(() => {
        loadUserData();
        initAmplitude();
        if (user?.email) {
            setAmplitudeUserId(user.email);
        }
        sendAmplitudeData("City Search", {});
    }, []);


    // Debug effect to log queriedRows changes
    useEffect(() => {
        console.log('queriedRows state changed:', queriedRows);
    }, [queriedRows]);


    // Effect to fix header colors after table renders
    useEffect(() => {
        const fixHeaderColors = () => {
            const table = document.querySelector('.city-search-table');
            if (table) {
                // EQUIPMENT (2nd column - after expandable arrow) - Gray
                // const equipmentHeader = table.querySelector('.rdt_TableHead .rdt_TableCol:nth-child(2)');
                // if (equipmentHeader) {
                //     equipmentHeader.style.backgroundColor = '#f8f9fa';
                //     equipmentHeader.style.background = '#f8f9fa';
                //     equipmentHeader.setAttribute('style', 'background-color: #f8f9fa !important; background: #f8f9fa !important;');
                // }


                // PICK CITY (3rd column) - Green
                // const pickCityHeader = table.querySelector('.rdt_TableHead .rdt_TableCol:nth-child(3)');
                // if (pickCityHeader) {
                //     // Use cssText for complete control
                //     pickCityHeader.style.cssText = 'background-color: #e8f5e8 !important; background: #e8f5e8 !important; color: #495057 !important; font-weight: 600 !important; font-size: 12px !important; text-transform: uppercase !important; padding: 12px 8px !important; text-align: center !important; border-bottom: 2px solid #dee2e6 !important;';
                // }


                // PICK RADIUS (4th column) - Make it exactly like PICK CITY
                // const pickRadiusHeader = table.querySelector('.rdt_TableHead .rdt_TableCol:nth-child(4)');
                // if (pickRadiusHeader) {
                //     // Copy the exact same styling as PICK CITY
                //     pickRadiusHeader.style.cssText = 'background-color: #e8f5e8 !important; background: #e8f5e8 !important; color: #495057 !important; font-weight: 600 !important; font-size: 12px !important; text-transform: uppercase !important; padding: 12px 8px !important; text-align: center !important; border-bottom: 2px solid #dee2e6 !important;';
                // }


                // DROP CITY (5th column) - Red
                // const dropCityHeader = table.querySelector('.rdt_TableHead .rdt_TableCol:nth-child(5)');
                // if (dropCityHeader) {
                //     dropCityHeader.style.backgroundColor = '#ffe8e8';
                //     dropCityHeader.style.background = '#ffe8e8';
                //     dropCityHeader.setAttribute('style', 'background-color: #ffe8e8 !important; background: #ffe8e8 !important;');
                // }


                // DROP RADIUS (6th column) - Red
                // const dropRadiusHeader = table.querySelector('.rdt_TableHead .rdt_TableCol:nth-child(6)');
                // if (dropRadiusHeader) {
                //     dropRadiusHeader.style.backgroundColor = '#f8d7da';
                //     dropRadiusHeader.style.background = '#f8d7da';
                //     dropRadiusHeader.setAttribute('style', 'background-color: #f8d7da !important; background: #f8d7da !important;');
                // }


                // ACTIONS (7th column) - Black
                // const actionsHeader = table.querySelector('.rdt_TableHead .rdt_TableCol:nth-child(7)');
                // if (actionsHeader) {
                //     actionsHeader.style.backgroundColor = '#343a40';
                //     actionsHeader.style.background = '#343a40';
                //     actionsHeader.style.color = 'white';
                //     actionsHeader.setAttribute('style', 'background-color: #343a40 !important; background: #343a40 !important; color: white !important;');
                // }
            }
        };


        // Run immediately and also after multiple delays to ensure table is rendered
        // fixHeaderColors();
        // const timeoutId1 = setTimeout(fixHeaderColors, 100);
        // const timeoutId2 = setTimeout(fixHeaderColors, 500);
        // const timeoutId3 = setTimeout(fixHeaderColors, 1000);
        
        // Also run continuously every 2 seconds to catch any dynamic changes
        // const intervalId = setInterval(fixHeaderColors, 2000);
        
        return () => {
            // clearTimeout(timeoutId1);
            // clearTimeout(timeoutId2);
            // clearTimeout(timeoutId3);
            // clearInterval(intervalId);
        };
    }, [searchHistoryData]);


    // Simple tooltip for expand buttons - using react-tooltip like LaneData.js


    if (loading) {
        return <PageLoader />;
    }


    if (!state.subscription) {
        return (
            <div className="bg-image">
                <div className="container-fluid">
                    <div className="text-center py-5">
                        <div className="alert alert-warning mx-auto" style={{maxWidth: '600px'}}>
                            <h4 className="alert-heading">Subscription Required</h4>
                            <p>You need an active subscription to access the City Search feature and view your search history.</p>
                            <hr />
                            <p className="mb-0">
                                Please contact your administrator or upgrade your subscription to continue.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }


    return (
        <div>
            <div className="bg-image">
                <div className="container-fluid">

               	    <div className="filter-row rate-search-filter-row">

               	        <div className="filter-group">
               	        	<label></label>
						</div>

               	        <div className="filter-group">
               	        	<label>EQUIPMENT</label>
							<Select
                            	options={equipmentOptions}
                                value={singleRowSearch.equipment ? {value: singleRowSearch.equipment, label: singleRowSearch.equipment} : null}
                                onChange={(option) => handleSingleRowSearchChange('equipment', option?.value || '')}
                                isSearchable={true}
                                placeholder="Select"
                        		className="filter-control equipment"
                                
                                // styles={{
                                // 	control: (provided) => ({
                                //     	...provided,
                                //     	backgroundColor: '#f8f9fa',
                                //     	border: '1px solid #dee2e6',
                                //     	borderRadius: '4px',
                                //     	minHeight: '38px'
                                // 	})
                                // }}
                            />
                        </div>

                	    <div className="filter-group">                	    	
                	    	<label className="pick-city">PICK CITY</label>
                            <AsyncSelect
                            	cacheOptions
								defaultOptions
                                loadOptions={loadCityOptions}
                                value={singleRowSearch.pickCity ? {value: singleRowSearch.pickCity, label: singleRowSearch.pickCity} : null}
                                onChange={(option) => {
                                	handleSingleRowSearchChange('pickCity', option?.value || '');
						        }}
                        		placeholder="Search..."
                                classNamePrefix="single-row-city-select"
                    			// styles={pickCityStyles}
                                styles={{
                                	control: (provided) => ({
                                		...provided,
                                	    backgroundColor: '#e8f5e8',
                                	    border: '1px solid #28a745',
                                	    borderRadius: '4px',
                                	    minHeight: '38px'
                                	})
                                }}
							/>
                	    
                	    </div>

                	    <div className="filter-group">

                	        <label className="pick">RADIUS</label>
                            {/*<label className="form-label fw-bold mb-2" style={{color: '#28a745'}}>RADIUS</label>*/}
                            <Select
                            	options={[
                                	// {value: '25', label: '25 miles'},
                                    {value: '50', label: '50 miles'},
                                    {value: '100', label: '100 miles'},
                                    {value: '150', label: '150 miles'},
                                    {value: '200', label: '200 miles'},
                                    {value: '250', label: '250 miles'}
                                ]}
                                            value={singleRowSearch.pickRadius ? {value: singleRowSearch.pickRadius, label: `${singleRowSearch.pickRadius} miles`} : null}
                                            onChange={(option) => handleSingleRowSearchChange('pickRadius', option?.value || '')}
                                            isSearchable={true}
                                            placeholder="Select"
                                            classNamePrefix="single-row-radius-select"
                                            menuPlacement="auto"
                                            isClearable={false}
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    backgroundColor: '#e8f5e8',
                                                    border: '1px solid #28a745',
                                                    borderRadius: '4px',
                                                    minHeight: '38px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    color: '#666',
                                                    fontSize: '14px'
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    backgroundColor: 'white',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    backgroundColor: state.isSelected ? '#28a745' : 
                                                                  state.isFocused ? '#e8f5e8' : 'white',
                                                    color: state.isSelected ? 'white' : '#333',
                                                    padding: '8px 12px'
                                                })
                                            }}
                                        />
                	        </div>

                	    <div className="filter-group">
                	    
                	    	<label className="drop-city">DROP CITY</label>
                                        {/*<label className="form-label fw-bold mb-2" style={{color: '#dc3545'}}>DROP CITY</label>*/}
                                        <AsyncSelect
                                            cacheOptions
                                            defaultOptions
                                            loadOptions={loadCityOptions}
                                            value={singleRowSearch.dropCity ? {value: singleRowSearch.dropCity, label: singleRowSearch.dropCity} : null}
                                            onChange={(option) => {
                                                handleSingleRowSearchChange('dropCity', option?.value || '');
                                            }}
                                            placeholder="Search..."
                                            classNamePrefix="single-row-city-select"
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    backgroundColor: '#ffe8e8',
                                                    border: '1px solid #dc3545',
                                                    borderRadius: '4px',
                                                    minHeight: '38px'
                                                })
                                            }}
                                        />
                	        </div>

                	        <div className="filter-group">
                	        	<label className="drop">RADIUS</label>
                                        {/*<label className="form-label fw-bold mb-2" style={{color: '#dc3545'}}>RADIUS</label>*/}
                                        <Select
                                            options={[
                                                // {value: '25', label: '25 miles'},
                                                {value: '50', label: '50 miles'},
                                                {value: '100', label: '100 miles'},
                                                {value: '150', label: '150 miles'},
                                                {value: '200', label: '200 miles'},
                                                {value: '250', label: '250 miles'}
                                            ]}
                                            value={singleRowSearch.dropRadius ? {value: singleRowSearch.dropRadius, label: `${singleRowSearch.dropRadius} miles`} : null}
                                            onChange={(option) => handleSingleRowSearchChange('dropRadius', option?.value || '')}
                                            isSearchable={true}
                                            placeholder="Select"
                                            classNamePrefix="single-row-radius-select"
                                            menuPlacement="auto"
                                            isClearable={false}
                                            styles={{
                                                control: (provided) => ({
                                                    ...provided,
                                                    backgroundColor: '#ffe8e8',
                                                    border: '1px solid #dc3545',
                                                    borderRadius: '4px',
                                                    minHeight: '38px'
                                                }),
                                                placeholder: (provided) => ({
                                                    ...provided,
                                                    color: '#666',
                                                    fontSize: '14px'
                                                }),
                                                menu: (provided) => ({
                                                    ...provided,
                                                    backgroundColor: 'white',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                }),
                                                option: (provided, state) => ({
                                                    ...provided,
                                                    backgroundColor: state.isSelected ? '#dc3545' : 
                                                                  state.isFocused ? '#ffe8e8' : 'white',
                                                    color: state.isSelected ? 'white' : '#333',
                                                    padding: '8px 12px'
                                                })
                                            }}
                                        />
                	        </div>

                	    <div className="query-group">
                    			
                	        <label>
                	        	{state.apiMessage?.quotaMessage 
                    				? <span>Your Quota: {state.apiMessage.quotaMessage}</span>
                    				: <span>&nbsp;</span>
                    			}                
                    		</label>	

               				<div>
                   				<button
                             			className="query-button"
                                       onClick={handleSingleRowSearch}
                                       disabled={singleRowSearching}
                                       >
                                          	{singleRowSearching ? (
                                              	<span className="spinner-border spinner-border-sm"></span>
                                          	) : (
                                              	<span>QUERY</span>
                                          	)}                                            
								</button>
							</div>

                		</div>

					</div>

                    



                    {/* Single Row Search Form */}
                    <div className="single-row-search-container mb-4">
                        <div className="card shadow-sm rate-search-filters">
                            <div className="card-body">
                                <div className="row align-items-end">
                                    
                                    <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                        
                                    </div>
                                    <div className="col-lg-2 col-md-3 col-sm-6 mb-3 d-flex align-items-end">
                                        
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/* Single Row Search Results */}
                        {singleRowResults && (
                            <div className="single-row-results mt-4">
                                <div className="card shadow-sm">
                                    <div className="card-body">
                                        {singleRowResults.error ? (
                                            <div className="alert alert-warning text-center">
                                                <h6>Search Failed</h6>
                                                <p>{singleRowResults.message}</p>
                                            </div>
                                        ) : (
                                            <>
                                                <h6 className="mb-3 text-center">
                                                    Query Results for: {singleRowSearch.equipment} | {singleRowSearch.pickCity} → {singleRowSearch.dropCity}
                                                </h6>
                                                
                                                {/* Price Blocks Row */}
                                                <div className="row mb-3 justify-content-center">



                                                    <div className="col-6 col-md-4 col-lg-2 mb-2">
                                                        <PriceBlockComponent 
                                                            object={{
                                                                days: '4 Months',
                                                                laneStatistics: singleRowResults.thirtyDaysAverage,
                                                                subscriptionType: state.subscriptionType,
                                                                onClick: () => openModal30Days(singleRowResults)
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-6 col-md-4 col-lg-2 mb-2">
                                                        <PriceBlockComponent 
                                                            object={{
                                                                days: '2 Months',
                                                                laneStatistics: singleRowResults.fifteenDaysAverage,
                                                                subscriptionType: state.subscriptionType,
                                                                onClick: () => openModal15Days(singleRowResults)
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-6 col-md-4 col-lg-2 mb-2">
                                                        <PriceBlockComponent 
                                                            object={{
                                                                days: '30 Days',
                                                                laneStatistics: singleRowResults.sevenDaysAverage,
                                                                subscriptionType: state.subscriptionType,
                                                                onClick: () => openModal7Days(singleRowResults)
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-6 col-md-4 col-lg-2 mb-2">
                                                        <PriceBlockComponent 
                                                            object={{
                                                                days: '7 Days',
                                                                laneStatistics: singleRowResults.threeDaysAverage,
                                                                subscriptionType: state.subscriptionType,
                                                                onClick: () => openModal3Days(singleRowResults)
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="col-6 col-md-4 col-lg-2 mb-2">
                                                        <PriceBlockComponent 
                                                            object={{
                                                                days: '3 Days',
                                                                laneStatistics: singleRowResults.oneDaysAverage,
                                                                subscriptionType: state.subscriptionType,
                                                                onClick: () => openModal1Days(singleRowResults)
                                                            }}
                                                        />
                                                    </div>
                                                </div>


                                                {/* Line Chart */}
                                                {singleRowResults.chartData && singleRowResults.chartData.length > 0 && (
                                                    <div className="row mt-4 justify-content-center">
                                                        <div className="col-12 col-xl-10">
                                                            <div className="card shadow-sm">
                                                                <div className="card-body">
                                                                    <h6 className="mb-3 text-center">Price Trends Over Time</h6>
                                                                    <Rmaps 
                                                                        ticker={{
                                                                            dataMaps: {
                                                                                mapsData: singleRowResults.chartData
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <header className="city-search-header">
                        {searchHistoryData.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="bg-white mx-auto p-4" style={{maxWidth: '600px'}}>
                                    <h4 className="mb-2">No Search History</h4>
                                    <p className="text-muted mb-2">You haven't performed any searches yet. Your search history will appear here once you start using the search features.</p>
                                    <p className="text-muted mb-0">
                                        Try searching for lanes in other parts of the application to build up your search history.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="d-flex flex-wrap align-items-center justify-content-between mb-4" style={{gap: '1rem'}}>
                                    <h4 className="search-history-header mb-0" style={{fontWeight: '400', color: '#666', marginLeft: '8px'}}>Search History</h4>
                                    <div
                                        className="toggle-switch"
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            const next = !confirmBeforeDelete;
                                            setConfirmBeforeDelete(next);
                                            try {
                                                localStorage.setItem('rateSearch_confirmBeforeDelete', String(next));
                                            } catch (err) { /* ignore */ }
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                const next = !confirmBeforeDelete;
                                                setConfirmBeforeDelete(next);
                                                try {
                                                    localStorage.setItem('rateSearch_confirmBeforeDelete', String(next));
                                                } catch (err) { /* ignore */ }
                                            }
                                        }}
                                        title={confirmBeforeDelete ? 'Confirm before delete: ON' : 'Confirm before delete: OFF'}
                                        style={{ position: 'relative', zIndex: 10 }}
                                    >
                                        <div className={`toggle-switch-control ${confirmBeforeDelete ? 'active' : ''}`}>
                                            <div className="toggle-switch-slider" />
                                        </div>
                                        <span className="toggle-switch-label" style={{textTransform: 'none', fontSize: '13px'}}>
                                            Confirm before delete
                                        </span>
                                    </div>
                                </div>
                                <DataTable
                                key="rate-search-history-table"
                                columns={columns}
                                data={searchHistoryData}
                                customStyles={customTableStyles}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[10, 25, 50]}
                                responsive={true}
                                fixedHeader={false}
                                highlightOnHover
                                pointerOnHover
                                expandableRows
                                expandableRowsComponent={ExpandableComponent}
                                expandableRowExpanded={row => {
                                    const isExpanded = expandedRows[row.id];
                                    console.log(`Row ${row.id} expanded:`, isExpanded);
                                    return isExpanded;
                                }}
                                expandableIcon={{
                                    collapsed: <span data-tooltip-id="expand-tooltip" data-tooltip-content="Click QUERY button to search and view results">▶</span>,
                                    expanded: <span data-tooltip-id="expand-tooltip" data-tooltip-content="Click QUERY button to search and view results">▼</span>
                                }}
                                noDataComponent={
                                    <div className="text-center py-4">
                                        <p className="text-muted">No search history found</p>
                                    </div>
                                }
                                className="city-search-table rate-search-table"
                                conditionalRowStyles={[
                                    {
                                        when: row => row.isNewEntry,
                                        style: {
                                            backgroundColor: '#e8f5e8',
                                            borderLeft: '4px solid #28a745'
                                        }
                                    }
                                ]}
                            />
                            
                            {/* Simple tooltip for expand buttons */}
                            <ReactTooltip
                                id="expand-tooltip"
                                content="Click QUERY button to search and view results"
                                place="top"
                                effect="solid"
                                backgroundColor="#333"
                                textColor="#fff"
                                style={{ zIndex: 9999 }}
                            />
                            </>
                        )}
                    </header>
                    <div className="rate-search-footer-spacer" />
                </div>
            </div>


            {/* REPORTS Modal */}
            <Modal
                isOpen={modalIsOpen}
                onAfterOpen={afterOpenModal}
                onRequestClose={closeModal}
                contentLabel="Reports Modal"
                style={{
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 1000
                    },
                    content: {
                        top: '50%',
                        left: '50%',
                        right: 'auto',
                        bottom: 'auto',
                        marginRight: '-50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: '1200px',
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }
                }}
            >

                <h2 className="margin-top-1rem">
                	Detailed Reports
                	<button className="my-button red h2-right" onClick={closeModal}>Close</button>
                </h2>

                {/*<img className="popup-logo" src={CompanyLogo} alt="Farelanes Logo" style={{width: '100px', height: 'auto'}}/>*/}
               	
                {/*<hr/>*/}

                <Tabs defaultIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>

                    <TabList className="gray-tabs">
                        <Tab className="gray-tab" eventKey="0" title="4 Months">120 Days</Tab>
                        <Tab className="gray-tab" eventKey="1" title="2 Months">60 Days</Tab>
                        <Tab className="gray-tab" eventKey="2" title="30 Days">30 Days</Tab>
                        <Tab className="gray-tab" eventKey="3" title="7 Days">7 Days</Tab>
                        <Tab className="gray-tab" eventKey="4" title="3 Days">3 Days</Tab>
                    </TabList>

                    <TabPanel>
                        {(() => {
                            const results = getCurrentResults();
                            const collectionData = getCollectionData(results?.thirtyDaysAverage);
                            console.log('4 Months TabPanel - results:', results);
                            console.log('4 Months TabPanel - thirtyDaysAverage:', results?.thirtyDaysAverage);
                            console.log('4 Months TabPanel - collection data:', collectionData);
                            console.log('4 Months TabPanel - collection length:', collectionData.length);
                            return null;
                        })()}
                        <div className="alert alert-success align-content-center align-farelanes-content-center">
                            <span>4 Months reports, total number of reports
                                &nbsp; <span className=""><strong>{getCurrentResults()?.thirtyDaysAverage?.documentCount || getCollectionData(getCurrentResults()?.thirtyDaysAverage).length || 0}</strong></span>
                            </span>
                        </div>

                        <DataTable
                            striped={true}
                            columns={reportColumns}
                            data={getCollectionData(getCurrentResults()?.thirtyDaysAverage)}
                            defaultSortAsc={false}
                            direction="auto"
                            fixedHeaderScrollHeight="300px"
                            pagination
                            responsive
                            subHeaderAlign="right"
                            subHeaderWrap
                            className="rate-search-reports-table"
                        />
                    </TabPanel>


                    <TabPanel>
                        <div className="alert alert-success align-content-center align-farelanes-content-center">
                            <span>2 Months reports, total number of reports
                                &nbsp; <span className=""><strong>{getCurrentResults()?.fifteenDaysAverage?.documentCount || getCollectionData(getCurrentResults()?.fifteenDaysAverage).length || 0}</strong></span>
                            </span>
                        </div>


                        <DataTable
                            striped={true}
                            columns={reportColumns}
                            data={getCollectionData(getCurrentResults()?.fifteenDaysAverage)}
                            defaultSortAsc={false}
                            direction="auto"
                            fixedHeaderScrollHeight="300px"
                            pagination
                            responsive
                            subHeaderAlign="right"
                            subHeaderWrap
                            className="rate-search-reports-table"
                        />
                    </TabPanel>


                    <TabPanel>
                        <div className="alert alert-success align-content-center align-farelanes-content-center">
                            <span>30 Days reports, total number of reports
                                &nbsp; <span className=""><strong>{getCurrentResults()?.sevenDaysAverage?.documentCount || getCollectionData(getCurrentResults()?.sevenDaysAverage).length || 0}</strong></span>
                            </span>
                        </div>


                        <DataTable
                            striped={true}
                            columns={reportColumns}
                            data={getCollectionData(getCurrentResults()?.sevenDaysAverage)}
                            defaultSortAsc={false}
                            direction="auto"
                            fixedHeaderScrollHeight="300px"
                            pagination
                            responsive
                            subHeaderAlign="right"
                            subHeaderWrap
                            className="rate-search-reports-table"
                        />
                    </TabPanel>


                    <TabPanel>
                        <div className="alert alert-success align-content-center align-farelanes-content-center">
                            <span>7 Days reports, total number of reports
                                &nbsp; <span className=""><strong>{getCurrentResults()?.threeDaysAverage?.documentCount || getCollectionData(getCurrentResults()?.threeDaysAverage).length || 0}</strong></span>
                            </span>
                        </div>


                        <DataTable
                            striped={true}
                            columns={reportColumns}
                            data={getCollectionData(getCurrentResults()?.threeDaysAverage)}
                            defaultSortAsc={false}
                            direction="auto"
                            fixedHeaderScrollHeight="300px"
                            pagination
                            responsive
                            subHeaderAlign="right"
                            subHeaderWrap
                            className="rate-search-reports-table"
                        />
                    </TabPanel>


                    <TabPanel>
                        <div className="alert alert-success align-content-center align-farelanes-content-center">
                            <span>3 Days reports, total number of reports
                                &nbsp; <span className=""><strong>{getCurrentResults()?.oneDaysAverage?.documentCount || getCollectionData(getCurrentResults()?.oneDaysAverage).length || 0}</strong></span>
                            </span>
                        </div>


                        <DataTable
                            striped={true}
                            columns={reportColumns}
                            data={getCollectionData(getCurrentResults()?.oneDaysAverage)}
                            defaultSortAsc={false}
                            direction="auto"
                            fixedHeaderScrollHeight="300px"
                            pagination
                            responsive
                            subHeaderAlign="right"
                            subHeaderWrap
                            className="rate-search-reports-table"
                        />
                    </TabPanel>
                </Tabs>
            </Modal>
        </div>
    );
};


export default withAuthenticationRequired(CitySearch, {
    onRedirecting: () => <PageLoader/>,
});